/**
 * ConsumablesFormDialog — bám đúng Access spec `frmFactoryQryConsumablesAdd`
 * (`plan/access_all/form_frmFactoryQryConsumablesAdd.txt`) +
 * `frmFactoryConsumablesSubForm` (btnNew/btnEdit/btnCopy).
 *
 * Layout (Access Left/Top coords):
 *   Left col (Left=2040):                Right col (Left=8496):
 *     mrn_or_mdn  (combo, T=252)           quantity     (numeric, T=252)
 *     mrn_mdn_no* (text,  T=636)           is_sent_bill (combo,  T=636)
 *     date        (date,  T=1020)          order_no     (text,   T=1020)
 *     supplier    (text,  T=1404)          status       (text,   T=1404)
 *     category*   (combo, T=1788)          note         (memo,   T=1788, H=696)
 *     truck_no    (text,  T=2172)
 *
 * Required (Access `strCheck = ",mrn_mdn_no,category,mrn_or_mdn,"`):
 *   mrn_mdn_no, category, mrn_or_mdn.
 *
 * Save payload (Access `strI_mainsub`, exact order): mrn_mdn_no, mrn_or_mdn,
 * date, supplier, category, truck_no, quantity, is_sent_bill, order_no, status,
 * note. (`strU_mainsub` = same minus mrn_mdn_no — PK; parent merges PK on edit.)
 *
 * Combos:
 *   mrn_or_mdn   ← static Value List "MDN;MRN".
 *   is_sent_bill ← static Value List "1;No;0;Yes" (bound value "1"=No, "0"=Yes).
 *   category     ← server combos.category (Access `SELECT category, unit FROM
 *                  FactoryMDQryMaterial` = tlc_factory.category_material).
 *
 * Modes (Access subform btnNew/btnEdit/btnCopy):
 *   new  → empty; mrn_or_mdn + mrn_mdn_no editable; chọn type → suggest mã.
 *   edit → hydrate row; mrn_mdn_no + mrn_or_mdn LOCKED (PK + derived type).
 *   copy → clone toàn bộ field; reset mrn_mdn_no (PK mới, re-suggest theo type).
 *          (Access btnCopy clear cả category nhưng chị Đẹp yêu cầu copy đầy đủ
 *           → chỉ reset mrn_mdn_no.)
 *
 * Suggest + duplicate guard (Access `mrn_or_mdn_AfterUpdate` →
 * `Suggest_IndexName(... & "consumables" & type)` + `mrn_mdn_no_AfterUpdate`
 * DCount): đổi type → regen mã; blur mã trùng active row khác → auto-regen +
 * inline notice (mirror ReceiveFormDialog race-safe flow).
 */
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  TextField,
  Typography,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'
import { useEffect, useMemo, useRef, useState } from 'react'

import type { FactoryCell, FactoryRowPayload } from '@/store/api/factoryApi'
import {
  useGetConsumablesCombosQuery,
  useLazyCheckMrnMdnIdExistsQuery,
  useLazyGetSuggestMrnMdnIdQuery,
} from '@/store/api/factoryApi'
import { FORM_GRID_2COL, HIDE_ON_MOBILE_INLINE, TEXT_TRUNCATE } from '@/theme/responsive'

type ConsumablesRow = { id: string | number } & Record<string, FactoryCell>

interface DraftState {
  mrn_or_mdn: string
  mrn_mdn_no: string
  date: string
  supplier: string
  category: string
  truck_no: string
  quantity: string
  is_sent_bill: string
  order_no: string
  status: string
  note: string
}

const EMPTY_DRAFT: DraftState = {
  mrn_or_mdn: '',
  mrn_mdn_no: '',
  date: '',
  supplier: '',
  category: '',
  truck_no: '',
  quantity: '',
  is_sent_bill: '',
  order_no: '',
  status: '',
  note: '',
}

/** mrn_or_mdn — Access `RowSource = "MDN;MRN"`. */
const TYPE_ENUM = ['MDN', 'MRN']

/** is_sent_bill — Access `RowSource = "1;No;0;Yes"` (bound value | display). */
interface ComboItem {
  value: string
  label: string
}
// Access RowSource "1;No;0;Yes" — cột Postgres `is_sent_bill` là BOOLEAN
// (memory feedback_factory_yesno_boolean_column): true ↔ Access 1 = "No",
// false ↔ Access 0 = "Yes". Draft lưu 'true'/'false' string; payload gửi
// boolean thật (gửi "1"/"0" → psycopg DatatypeMismatch 503).
const SENT_BILL_OPTIONS: ComboItem[] = [
  { value: 'true', label: 'No' },
  { value: 'false', label: 'Yes' },
]

const titleBtnSx = {
  py: 0.25,
  px: 1,
  minHeight: 28,
  fontSize: '0.75rem',
  lineHeight: 1.2,
  textTransform: 'none' as const,
}

const compactInputSx = {
  '& .MuiInputBase-root': { minHeight: 32, fontSize: '0.8125rem' },
  '& .MuiInputBase-input': { py: 0.5, fontSize: '0.8125rem' },
  '& .MuiInputLabel-root': { fontSize: '0.8125rem', fontWeight: 500 },
  '& .MuiInputLabel-shrink': { fontWeight: 600 },
}

// Readonly bg standard (skill /convert-form): neutral gray for Access
// `Enabled = NotDefault` (locked) fields.
const readonlyFieldSx = {
  '& .MuiInputBase-root': {
    bgcolor: (theme: { palette: { mode: string } }) =>
      theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : '#f1f3f5',
  },
  '& .MuiInputBase-input': { color: 'text.secondary', fontWeight: 600 },
}

// Access Format `#,#00` → right-aligned thousands.
const numericInputProps = {
  style: { textAlign: 'right' as const },
  inputMode: 'decimal' as const,
}

// Parse "1,234.5" / "1234" / "" → number | null (strip thousand-sep commas).
function parseNumeric(v: string): number | null {
  const s = (v ?? '').toString().replace(/,/g, '').trim()
  if (!s) return null
  const n = parseFloat(s)
  return Number.isFinite(n) ? n : null
}

// Access quantity Format `#,#00` — thousand separator, decimals chỉ hiện khi
// có (tối đa 2). KHÔNG ép `.00` như Format "Standard".
function formatQty(v: number | string | null | undefined): string {
  if (v === null || v === undefined || v === '') return ''
  const n = typeof v === 'number' ? v : parseNumeric(String(v))
  if (n == null) return ''
  return n.toLocaleString('en-US', { maximumFractionDigits: 2 })
}

interface ConsumablesFormDialogProps {
  open: boolean
  /** 'new' = empty + suggest on type; 'edit' = hydrate, mrn_mdn_no/mrn_or_mdn
   *  locked; 'copy' = clone toàn bộ field, chỉ reset mrn_mdn_no (PK) + re-suggest. */
  mode: 'new' | 'edit' | 'copy'
  row: ConsumablesRow | null
  saving: boolean
  /** Active rows hiện có — dùng cho duplicate check tức thời (parity Access
   *  `mrn_mdn_no_AfterUpdate` disable Save khi active dup). */
  existingRows: ConsumablesRow[]
  factoryCode: string
  onClose: () => void
  onSave: (payload: FactoryRowPayload) => void
}

export function ConsumablesFormDialog({
  open,
  mode,
  row,
  saving,
  existingRows,
  factoryCode,
  onClose,
  onSave,
}: ConsumablesFormDialogProps) {
  const [draft, setDraft] = useState<DraftState>(EMPTY_DRAFT)
  // Inline notice khi mã bị auto-regen do trùng (mirror ReceiveFormDialog).
  const [regenNotice, setRegenNotice] = useState<{ from: string; to: string } | null>(null)
  const [precheckBusy, setPrecheckBusy] = useState(false)
  // Sync mirror của precheckBusy — chặn Save fire trước khi blur-precheck xong.
  const precheckBusyRef = useRef(false)

  const [triggerSuggest, suggestState] = useLazyGetSuggestMrnMdnIdQuery()
  const [triggerCheck] = useLazyCheckMrnMdnIdExistsQuery()

  const combosQuery = useGetConsumablesCombosQuery({ factoryCode }, { skip: !open })
  const categoryOptions = useMemo(
    () => combosQuery.data?.category ?? [],
    [combosQuery.data],
  )

  /** Hydrate draft — deps `[open, mode, row]` only (memory: hydrate deps). */
  useEffect(() => {
    if (!open) return
    if ((mode === 'edit' || mode === 'copy') && row) {
      setDraft({
        mrn_or_mdn: String(row.mrn_or_mdn ?? ''),
        // Copy: Access btnCopy clears mrn_mdn_no (re-suggest below).
        mrn_mdn_no: mode === 'copy' ? '' : String(row.mrn_mdn_no ?? ''),
        date: String(row.date ?? '').slice(0, 10),
        supplier: String(row.supplier ?? ''),
        // Copy: clone category (deviation từ Access btnCopy `category=""` —
        // chị Đẹp yêu cầu copy đầy đủ dòng; chỉ mrn_mdn_no reset vì là PK mới).
        category: String(row.category ?? ''),
        truck_no: String(row.truck_no ?? ''),
        // Pre-format Standard `#,#00` để Edit/Copy hiển thị thống nhất (anti #32).
        quantity: formatQty(row.quantity as number | string | null),
        is_sent_bill: row.is_sent_bill != null ? String(row.is_sent_bill) : '',
        order_no: String(row.order_no ?? ''),
        status: String(row.status ?? ''),
        note: String(row.note ?? ''),
      })
    } else {
      setDraft({
        ...EMPTY_DRAFT,
        date: new Date().toISOString().slice(0, 10),
      })
    }
    setRegenNotice(null)
    setPrecheckBusy(false)
    precheckBusyRef.current = false
  }, [open, mode, row])

  /** Copy: re-suggest mã theo type cloned (Access btnCopy giữ mrn_or_mdn,
   *  clear mrn_mdn_no → AfterUpdate-equivalent suggest). New không suggest ở
   *  đây (type rỗng → user chọn → handleTypeChange suggest). Edit không suggest. */
  useEffect(() => {
    if (!open || mode !== 'copy' || !row) return
    const typ = String(row.mrn_or_mdn ?? '').trim().toUpperCase()
    if (typ !== 'MRN' && typ !== 'MDN') return
    let cancelled = false
    void (async () => {
      try {
        const res = await triggerSuggest({ factoryCode, mrnOrMdn: typ }, false).unwrap()
        if (cancelled) return
        const m = (res.mrn_mdn_no || '').trim()
        if (m) setDraft((prev) => (prev.mrn_mdn_no.trim().length ? prev : { ...prev, mrn_mdn_no: m }))
      } catch {
        // Suggest fail (network/permission) — user nhập tay; BE raise nếu trùng.
      }
    })()
    return () => {
      cancelled = true
    }
  }, [open, mode, row, factoryCode, triggerSuggest])

  /** mrn_or_mdn change (Access `mrn_or_mdn_AfterUpdate` → MRN_MDN): set type +
   *  regen mã (overwrite — Access set unconditionally). Edit không đổi type. */
  const handleTypeChange = async (value: string) => {
    setDraft((prev) => ({ ...prev, mrn_or_mdn: value }))
    setRegenNotice(null)
    if (mode === 'edit') return
    if (value !== 'MRN' && value !== 'MDN') return
    try {
      const res = await triggerSuggest({ factoryCode, mrnOrMdn: value }, false).unwrap()
      const m = (res.mrn_mdn_no || '').trim()
      if (m) setDraft((prev) => ({ ...prev, mrn_or_mdn: value, mrn_mdn_no: m }))
    } catch {
      // Suggest fail — giữ field cho user nhập tay.
    }
  }

  /** Access `mrn_mdn_no_AfterUpdate` — blur field: nếu mã đã có active row
   *  khác dùng → regen mã mới + inline notice. Edit không check (PK cố định). */
  const handleMrnBlur = async () => {
    if (mode === 'edit') return
    const m = draft.mrn_mdn_no.trim()
    const typ = draft.mrn_or_mdn.trim().toUpperCase()
    if (!m) return
    precheckBusyRef.current = true
    setPrecheckBusy(true)
    try {
      const check = await triggerCheck({ factoryCode, mrnMdnNo: m }).unwrap()
      if (!check.exists) return
      if (typ !== 'MRN' && typ !== 'MDN') return
      // force network refetch — cache có thể giữ mã trùng từ lần suggest trước.
      const next = await triggerSuggest({ factoryCode, mrnOrMdn: typ }, false).unwrap()
      const nm = (next.mrn_mdn_no || '').trim()
      if (nm && nm !== m) {
        setDraft((prev) => ({ ...prev, mrn_mdn_no: nm }))
        setRegenNotice({ from: m, to: nm })
      }
    } catch {
      // Network/permission lỗi — bỏ qua, BE raise lúc Save nếu thật sự trùng.
    } finally {
      precheckBusyRef.current = false
      setPrecheckBusy(false)
    }
  }

  /** Clear regen notice khi user tự sửa mã (khác mã đã regen). */
  useEffect(() => {
    if (regenNotice && draft.mrn_mdn_no !== regenNotice.to) setRegenNotice(null)
  }, [draft.mrn_mdn_no, regenNotice])

  const selectedCategory = useMemo<ComboItem | null>(() => {
    const v = draft.category.trim()
    if (!v) return null
    const found = categoryOptions.find((o) => o.value === v)
    return found ? { value: found.value, label: found.label } : { value: v, label: v }
  }, [draft.category, categoryOptions])

  const selectedSentBill = useMemo<ComboItem | null>(() => {
    const v = draft.is_sent_bill.trim()
    if (!v) return null
    return SENT_BILL_OPTIONS.find((o) => o.value === v) ?? null
  }, [draft.is_sent_bill])

  /** Duplicate tức thời trên active rows (parity Access disable Save). Active
   *  cho new/copy (cả 2 insert mã mới). Loại trừ chính row khi edit (nhưng edit
   *  field locked nên không trùng). */
  const duplicateMrn = useMemo(() => {
    if (mode === 'edit') return false
    const m = draft.mrn_mdn_no.trim()
    if (!m) return false
    return existingRows.some(
      (r) => String(r.mrn_mdn_no ?? '').trim() === m && Number(r.is_status ?? 0) === 0,
    )
  }, [draft.mrn_mdn_no, existingRows, mode])

  const typeEmpty = draft.mrn_or_mdn.trim().length === 0
  const mrnEmpty = draft.mrn_mdn_no.trim().length === 0
  const categoryEmpty = draft.category.trim().length === 0
  const canSave =
    !typeEmpty && !mrnEmpty && !categoryEmpty && !duplicateMrn && !precheckBusy && !saving

  const handleChange = <K extends keyof DraftState>(key: K, value: string) =>
    setDraft((prev) => ({ ...prev, [key]: value }))

  // Auto-format quantity sang Standard `#,#00` khi blur (anti #34: format on
  // blur, không on keystroke). Typing raw → blur normalize.
  const handleQuantityBlur = () => {
    const f = formatQty(draft.quantity)
    if (f !== draft.quantity) setDraft((prev) => ({ ...prev, quantity: f }))
  }

  const buildPayload = (): FactoryRowPayload => {
    const str = (v: string): string | null => {
      const s = v.trim()
      return s.length ? s : null
    }
    // Exact order theo Access strI_mainsub.
    return {
      mrn_mdn_no: draft.mrn_mdn_no.trim(),
      mrn_or_mdn: draft.mrn_or_mdn.trim(),
      date: str(draft.date),
      supplier: str(draft.supplier),
      category: draft.category.trim(),
      truck_no: str(draft.truck_no),
      quantity: parseNumeric(draft.quantity),
      // BOOLEAN column — gửi true/false thật (không phải "1"/"0" string).
      is_sent_bill: draft.is_sent_bill === '' ? null : draft.is_sent_bill === 'true',
      order_no: str(draft.order_no),
      status: str(draft.status),
      note: str(draft.note),
    }
  }

  const handleSave = () => {
    if (precheckBusyRef.current) return
    if (!canSave) return
    onSave(buildPayload())
  }

  const titleVerb = mode === 'new' ? 'New' : mode === 'copy' ? 'Copy' : 'Edit'

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (saving) return
        if (reason === 'backdropClick') return
        onClose()
      }}
      maxWidth="md"
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: {
          maxHeight: 'min(720px, 92vh)',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <DialogTitle
        sx={{ pb: 1, pt: 1.25, display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}
      >
        <Typography
          component="span"
          noWrap
          title={`${titleVerb} — Factory · Consumables`}
          sx={{ ...TEXT_TRUNCATE, flex: '1 1 auto', fontWeight: 700, fontSize: '1rem', minWidth: 0 }}
        >
          {titleVerb} —
          <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>
            {' Factory ·'}
          </Box>
          {' Consumables'}
        </Typography>
        <Typography component="span" variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
          {factoryCode.toUpperCase()}
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', ml: 'auto', flexShrink: 0 }}>
          <Button
            type="button"
            variant="contained"
            size="small"
            color="success"
            onClick={handleSave}
            disabled={!canSave}
            sx={titleBtnSx}
          >
            {saving ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button
            type="button"
            variant="outlined"
            size="small"
            onClick={onClose}
            disabled={saving}
            sx={titleBtnSx}
          >
            Cancel
          </Button>
        </Box>
      </DialogTitle>
      <Divider sx={{ flexShrink: 0 }} />
      <DialogContent
        dividers
        sx={{ ...compactInputSx, overflowY: 'auto', flex: '1 1 auto', minHeight: 0, pt: 1.5 }}
      >
        {regenNotice ? (
          <Alert severity="warning" sx={{ mb: 1.25, py: 0.25 }}>
            {`MRN/MDN ${regenNotice.from} was just created — suggested new code ${regenNotice.to}.`}
          </Alert>
        ) : null}
        {duplicateMrn ? (
          <Alert severity="error" sx={{ mb: 1.25, py: 0.25 }}>
            This MRN/MDN number already exists. Pick another before saving.
          </Alert>
        ) : null}

        {/* 2-col grid bám Access Left=2040 (trái) / Left=8496 (phải), rows theo
            Top. gridRow/gridColumn explicit per field (memory: form grid align). */}
        <Box sx={FORM_GRID_2COL}>
          {/* Row 1 (T=252) — mrn_or_mdn * | quantity */}
          <Autocomplete<string, false, false, false>
            sx={{ gridColumn: { xs: 1, sm: 1 }, gridRow: { sm: 1 } }}
            size="small"
            disabled={mode === 'edit'}
            options={TYPE_ENUM}
            value={draft.mrn_or_mdn || null}
            onChange={(_, v) => void handleTypeChange(v ?? '')}
            renderInput={(params) => (
              <TextField
                {...params}
                label="MRN/MDN"
                required
                fullWidth
                autoFocus={mode !== 'edit'}
                helperText={mode === 'edit' ? undefined : 'Pick type to suggest the next number.'}
                sx={mode === 'edit' ? readonlyFieldSx : undefined}
              />
            )}
          />
          <TextField
            sx={{ gridColumn: { xs: 1, sm: 2 }, gridRow: { sm: 1 } }}
            label="Quantity"
            size="small"
            fullWidth
            type="text"
            value={draft.quantity}
            onChange={(e) => handleChange('quantity', e.target.value)}
            onBlur={handleQuantityBlur}
            inputProps={numericInputProps}
          />

          {/* Row 2 (T=636) — mrn_mdn_no * | is_sent_bill */}
          <TextField
            sx={{ gridColumn: { xs: 1, sm: 1 }, gridRow: { sm: 2 }, ...(mode === 'edit' ? readonlyFieldSx : {}) }}
            label="MRN/MDN No."
            size="small"
            fullWidth
            required
            disabled={mode === 'edit'}
            value={draft.mrn_mdn_no}
            onChange={(e) => handleChange('mrn_mdn_no', e.target.value)}
            onBlur={() => void handleMrnBlur()}
            error={duplicateMrn}
            helperText={
              mode === 'edit'
                ? undefined
                : precheckBusy
                  ? 'Verifying MRN/MDN code…'
                  : suggestState.isFetching
                    ? 'Suggesting next number…'
                    : 'Suggested from the type; edit if needed.'
            }
            InputProps={{
              endAdornment: precheckBusy ? <CircularProgress size={14} /> : null,
            }}
          />
          <Autocomplete<ComboItem, false, false, false>
            sx={{ gridColumn: { xs: 1, sm: 2 }, gridRow: { sm: 2 } }}
            size="small"
            options={SENT_BILL_OPTIONS}
            value={selectedSentBill}
            getOptionLabel={(o) => o.label}
            isOptionEqualToValue={(o, v) => o.value === v.value}
            onChange={(_, v) => handleChange('is_sent_bill', v?.value ?? '')}
            renderInput={(params) => <TextField {...params} label="Sent Bill?" fullWidth />}
          />

          {/* Row 3 (T=1020) — date | order_no */}
          <DatePicker
            label="Date"
            format="DD-MMM-YYYY"
            value={draft.date ? dayjs(draft.date) : null}
            onChange={(d) => handleChange('date', d && d.isValid() ? d.format('YYYY-MM-DD') : '')}
            slotProps={{
              textField: {
                size: 'small',
                fullWidth: true,
                InputLabelProps: { shrink: true },
                sx: { gridColumn: { xs: 1, sm: 1 }, gridRow: { sm: 3 } },
              },
            }}
          />
          <TextField
            sx={{ gridColumn: { xs: 1, sm: 2 }, gridRow: { sm: 3 } }}
            label="Order No"
            size="small"
            fullWidth
            value={draft.order_no}
            onChange={(e) => handleChange('order_no', e.target.value)}
          />

          {/* Row 4 (T=1404) — supplier | status */}
          <TextField
            sx={{ gridColumn: { xs: 1, sm: 1 }, gridRow: { sm: 4 } }}
            label="Supplier"
            size="small"
            fullWidth
            value={draft.supplier}
            onChange={(e) => handleChange('supplier', e.target.value)}
          />
          <TextField
            sx={{ gridColumn: { xs: 1, sm: 2 }, gridRow: { sm: 4 } }}
            label="Status"
            size="small"
            fullWidth
            value={draft.status}
            onChange={(e) => handleChange('status', e.target.value)}
          />

          {/* Row 5 (T=1788) — category * | note (memo, spans rows 5-6).
              Access LimitToList=True → strict lookup (KHÔNG freeSolo, anti #43);
              value === label (category) → collapse 1 dòng (rule #44). */}
          <Autocomplete<ComboItem, false, false, false>
            sx={{ gridColumn: { xs: 1, sm: 1 }, gridRow: { sm: 5 } }}
            size="small"
            options={categoryOptions.map((o) => ({ value: o.value, label: o.label }))}
            loading={combosQuery.isFetching}
            value={selectedCategory}
            getOptionLabel={(o) => (o.value === o.label ? o.value : `${o.value} — ${o.label}`)}
            isOptionEqualToValue={(o, v) => o.value === v.value}
            onChange={(_, v) => handleChange('category', v?.value ?? '')}
            renderInput={(params) => (
              <TextField {...params} label="Category" required fullWidth />
            )}
          />
          <TextField
            sx={{
              gridColumn: { xs: 1, sm: 2 },
              gridRow: { sm: '5 / span 2' },
              '& .MuiInputBase-root': { height: '100%', alignItems: 'flex-start' },
            }}
            label="Note"
            size="small"
            fullWidth
            multiline
            minRows={2}
            value={draft.note}
            onChange={(e) => handleChange('note', e.target.value)}
            InputProps={{ sx: { height: '100%', alignItems: 'stretch' } }}
          />

          {/* Row 6 (T=2172) — truck_no | (note continues) */}
          <TextField
            sx={{ gridColumn: { xs: 1, sm: 1 }, gridRow: { sm: 6 } }}
            label="Truck No"
            size="small"
            fullWidth
            value={draft.truck_no}
            onChange={(e) => handleChange('truck_no', e.target.value)}
          />
        </Box>
      </DialogContent>
    </Dialog>
  )
}

export default ConsumablesFormDialog
